/**
 * In-app tanpura drone (FR-6, Mode 1). Primary backend: sample playback of a
 * real tanpura recording (one authentic loop per pitch class — see
 * `tanpura-samples.ts` and `public/audio/tanpura/`), pitch-shifted by
 * playback rate to the requested Sa and looped seamlessly. When monitored on
 * headphones the drone never reaches the mic, so no separation is needed.
 *
 * If the sample can't be fetched/decoded (e.g. offline before the asset is
 * cached by the service worker, or a decode error), this falls back to the
 * original synthesized Karplus-Strong + formant-resonator-bank drone
 * (`TanpuraFallbackSynth`, backed by `tanpura-string-processor.ts` /
 * `tanpura-constants.ts`) so the drone still works, just less authentically.
 */
import tanpuraProcessorUrl from './tanpura-string-processor.ts?worker&url';
import {
  DEFAULT_TEMPO_BPM,
  DETUNE_CENTS,
  MASTER_LOWPASS_HZ,
  REVERB_DURATION_S,
  REVERB_DECAY_EXPONENT,
  REVERB_MIX,
} from './tanpura-constants.js';
import type { TanpuraProcessorOptions, TanpuraPluckMessage } from './tanpura-constants.js';
import { resolveTanpuraSample } from './tanpura-samples.js';

export interface TanpuraOptions {
  firstString?: 'Pa' | 'Ma';
  volume?: number;
  tempoBpm?: number;
}

/** Default gain applied when the caller doesn't pass `volume`. */
const DEFAULT_VOLUME = 0.2;

/** Fade-in on start / fade-out on stop, so looping/stopping the sample never
 * clicks (the recording's own loop seam is already crossfaded; this is only
 * for the synth's own start/stop transient). */
const FADE_IN_S = 0.08;
const FADE_OUT_S = 0.12;

/** Live volume-change ramp (setVolume), short enough to feel immediate. */
const VOLUME_RAMP_S = 0.05;

/** Very gentle warmth lowpass on top of the (already real, already-warm)
 * recording — high enough to be barely audible, just rounding off any
 * encoding artifacts at the very top end. */
const SAMPLE_LOWPASS_HZ = 12000;

/** Decoded tanpura loops, cached per filename (module-level, shared across
 * `TanpuraSynth` instances) so repeated starts — and starts once the file is
 * already in the browser's HTTP/SW cache — are instant instead of re-fetching
 * + re-decoding every time. Caches the in-flight promise (not just the
 * resolved buffer) so concurrent `start()` calls converge on one fetch. An
 * `AudioBuffer` produced by one `AudioContext`'s `decodeAudioData` is plain
 * PCM data and can be used as the `.buffer` of an `AudioBufferSourceNode`
 * created by any other `AudioContext`, so this cache is safe to share across
 * the fresh contexts a fallback/retry path may create. */
const sampleBufferCache = new Map<string, Promise<AudioBuffer>>();

async function loadTanpuraBuffer(ctx: AudioContext, file: string): Promise<AudioBuffer> {
  let pending = sampleBufferCache.get(file);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(`/audio/tanpura/${file}`);
      if (!response.ok) {
        throw new Error(`tanpura sample fetch failed (${response.status}): ${file}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      return ctx.decodeAudioData(arrayBuffer);
    })();
    sampleBufferCache.set(file, pending);
  }
  try {
    return await pending;
  } catch (err) {
    // Don't poison the cache forever — a later start (e.g. once back online)
    // should get to retry the fetch rather than replaying the same failure.
    sampleBufferCache.delete(file);
    throw err;
  }
}

/** Synthesizes a stereo reverb impulse response: exponentially-decaying noise,
 * a small, cheap stand-in for room/body resonance (see tanpura-constants.ts).
 * Only used by the fallback synth below. */
function buildReverbImpulse(ctx: AudioContext): AudioBuffer {
  const length = Math.max(1, Math.round(ctx.sampleRate * REVERB_DURATION_S));
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < impulse.numberOfChannels; ch++) {
    const data = impulse.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const t = i / length;
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, REVERB_DECAY_EXPONENT);
    }
  }
  return impulse;
}

/**
 * The original synthesized tanpura: a Karplus-Strong plucked-string model (4
 * strings, Pa/Ma-Sa-Sa-Sa̠) run through a jivari-formant resonator bank. Kept
 * as the fallback when real-sample playback (`TanpuraSynth` below) can't load
 * its audio asset. See `tanpura-constants.ts` for the acoustic model + every
 * tunable constant.
 */
export class TanpuraFallbackSynth {
  private ctx: AudioContext | undefined;
  private node: AudioWorkletNode | undefined;
  private lowpass: BiquadFilterNode | undefined;
  private dryGain: GainNode | undefined;
  private wetGain: GainNode | undefined;
  private convolver: ConvolverNode | undefined;
  private master: GainNode | undefined;
  private timer: ReturnType<typeof setInterval> | undefined;
  private cycleLength = 0;
  private step = 0;
  // Synchronous in-flight guard: `this.ctx` isn't assigned until *after* the
  // `await ctx.audioWorklet.addModule(...)` below settles, so two overlapping
  // `start()` calls (e.g. a fast double-tap, or a deep-link start racing the
  // button's own click) would both pass a `this.ctx`-only check, both build a
  // worklet graph, and leak/double one of them. Set synchronously before any
  // `await` so the second caller bails immediately instead.
  private starting = false;

  get running(): boolean {
    return this.ctx !== undefined;
  }

  async start(saHz: number, options: TanpuraOptions = {}): Promise<void> {
    if (this.ctx || this.starting) return;
    this.starting = true;
    try {
      const ctx = new AudioContext();
      await ctx.resume();
      await ctx.audioWorklet.addModule(tanpuraProcessorUrl);

      // Pluck order Pa(or Ma)-Sa-Sa-Sa̠ (5-8-8-1 tuning; see tanpura-constants.ts).
      // The two Sa strings are detuned a few cents apart for a living, breathing
      // beat rather than a static unison.
      const first = options.firstString === 'Ma' ? saHz * (4 / 3) : saHz * (3 / 2);
      const detuneRatio = Math.pow(2, DETUNE_CENTS / 1200);
      const frequencies = [first, saHz, saHz * detuneRatio, saHz / 2];
      this.cycleLength = frequencies.length;

      const processorOptions: TanpuraProcessorOptions = { frequencies };
      const node = new AudioWorkletNode(ctx, 'tanpura-string-processor', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2],
        processorOptions,
      });

      // Graph: worklet -> gentle lowpass (warmth) -> [dry] + [reverb] -> master.
      const lowpass = ctx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = MASTER_LOWPASS_HZ;

      const dryGain = ctx.createGain();
      dryGain.gain.value = 1 - REVERB_MIX;
      const wetGain = ctx.createGain();
      wetGain.gain.value = REVERB_MIX;
      const convolver = ctx.createConvolver();
      convolver.normalize = true;
      convolver.buffer = buildReverbImpulse(ctx);

      const master = ctx.createGain();
      master.gain.value = options.volume ?? DEFAULT_VOLUME;

      node.connect(lowpass);
      lowpass.connect(dryGain);
      lowpass.connect(convolver);
      convolver.connect(wetGain);
      dryGain.connect(master);
      wetGain.connect(master);
      master.connect(ctx.destination);

      this.ctx = ctx;
      this.node = node;
      this.lowpass = lowpass;
      this.dryGain = dryGain;
      this.wetGain = wetGain;
      this.convolver = convolver;
      this.master = master;
      this.step = 0;

      const period = 60 / (options.tempoBpm ?? DEFAULT_TEMPO_BPM); // seconds between plucks
      this.pluck();
      this.timer = setInterval(() => this.pluck(), period * 1000);
    } finally {
      this.starting = false;
    }
  }

  private pluck(): void {
    const node = this.node;
    if (!node) return;
    const index = this.step % this.cycleLength;
    this.step++;
    const msg: TanpuraPluckMessage = { type: 'pluck', index };
    node.port.postMessage(msg);
  }

  setVolume(v: number): void {
    if (this.master) this.master.gain.value = v;
  }

  async stop(): Promise<void> {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    this.node?.port.close();
    this.node?.disconnect();
    this.lowpass?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.convolver?.disconnect();
    this.master?.disconnect();
    await this.ctx?.close();
    this.ctx = undefined;
    this.node = undefined;
    this.lowpass = undefined;
    this.dryGain = undefined;
    this.wetGain = undefined;
    this.convolver = undefined;
    this.master = undefined;
  }
}

/**
 * Public tanpura drone API (unchanged surface from the pre-sample synth):
 * `start`/`stop`/`setVolume`/`running`. Backed by real-recording sample
 * playback; transparently falls back to `TanpuraFallbackSynth` if the sample
 * can't be loaded.
 */
export class TanpuraSynth {
  private ctx: AudioContext | undefined;
  private source: AudioBufferSourceNode | undefined;
  private gain: GainNode | undefined;
  private lowpass: BiquadFilterNode | undefined;
  private fallback: TanpuraFallbackSynth | undefined;
  private isRunning = false;
  // Synchronous in-flight guard, set before any `await` below — `isRunning`
  // alone isn't enough because it's only flipped true *after* the fetch/
  // decode chain resolves, so two overlapping `start()` calls (a fast
  // double-tap, or the `?tanpura=1` deep-link racing the drone button's own
  // click) would both pass an `isRunning`-only check and both build a
  // source->...->destination graph, with the second silently overwriting
  // `this.source`/`this.gain` and leaking the first's nodes.
  private starting = false;

  get running(): boolean {
    return this.isRunning;
  }

  async start(saHz: number, options: TanpuraOptions = {}): Promise<void> {
    if (this.isRunning || this.starting) return;
    this.starting = true;
    const volume = options.volume ?? DEFAULT_VOLUME;

    try {
      await this.startSample(saHz, volume);
      this.isRunning = true;
    } catch (err) {
      console.warn(
        '[tanpura] real-sample drone failed to load, falling back to synthesized drone:',
        err,
      );
      // The failed sample attempt may have left a still-open `this.ctx`
      // behind — either one reused from an earlier successful start (this
      // attempt didn't own it, so `startSample`'s own catch didn't close it)
      // or, in principle, one this attempt itself would have taken over on
      // success. Either way, we're about to switch to the fallback's own
      // AudioContext, so close the sample context now rather than stranding
      // it open for the rest of the session.
      if (this.ctx) {
        await this.ctx.close().catch(() => {});
        this.ctx = undefined;
      }
      const fallback = new TanpuraFallbackSynth();
      await fallback.start(saHz, options);
      this.fallback = fallback;
      this.isRunning = true;
    } finally {
      this.starting = false;
    }
  }

  /** `firstString` is intentionally unused here: the recording already
   * captures the full Pa-Sa-Sa-Sa̠ tanpura cycle, so there's no separate
   * "first string" to choose — the option is accepted for API compatibility
   * with the fallback synth (which still needs it) and ignored. */
  private async startSample(saHz: number, volume: number): Promise<void> {
    // Reuse a context from a previous successful sample start if one exists
    // (kept alive across stop() for fast restarts); otherwise create one now,
    // on this call's user gesture.
    let ctx = this.ctx;
    const ownsNewCtx = ctx === undefined;
    if (!ctx) ctx = new AudioContext();

    try {
      await ctx.resume();
      const { file, playbackRate } = resolveTanpuraSample(saHz);
      const buffer = await loadTanpuraBuffer(ctx, file);

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.playbackRate.value = playbackRate;

      const lowpass = ctx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = SAMPLE_LOWPASS_HZ;

      const gain = ctx.createGain();
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume, now + FADE_IN_S);

      source.connect(lowpass);
      lowpass.connect(gain);
      gain.connect(ctx.destination);
      source.start();

      this.ctx = ctx;
      this.source = source;
      this.gain = gain;
      this.lowpass = lowpass;
    } catch (err) {
      if (ownsNewCtx) await ctx.close().catch(() => {});
      throw err;
    }
  }

  setVolume(v: number): void {
    if (this.fallback) {
      this.fallback.setVolume(v);
      return;
    }
    if (this.gain && this.ctx) {
      const now = this.ctx.currentTime;
      this.gain.gain.cancelScheduledValues(now);
      this.gain.gain.setValueAtTime(this.gain.gain.value, now);
      this.gain.gain.linearRampToValueAtTime(v, now + VOLUME_RAMP_S);
    }
  }

  async stop(): Promise<void> {
    if (this.fallback) {
      await this.fallback.stop();
      this.fallback = undefined;
      this.isRunning = false;
      return;
    }

    const { ctx, source, gain } = this;
    if (!ctx || !source || !gain) {
      this.isRunning = false;
      return;
    }

    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + FADE_OUT_S);
    await new Promise((resolve) => setTimeout(resolve, FADE_OUT_S * 1000));

    source.stop();
    source.disconnect();
    this.lowpass?.disconnect();
    gain.disconnect();

    this.source = undefined;
    this.gain = undefined;
    this.lowpass = undefined;
    this.isRunning = false;
    // `ctx` itself is intentionally kept alive for a fast subsequent start().
  }
}
